import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { appContextSelector } from 'modules/app/appSlice';
import { showToast } from 'modules/app/toasterSlice';
import { currentUserSelector } from 'modules/user/userSlice';

import { AppThunk, RootState } from 'store';
import { fixFloat, roundNumberToTwo } from 'utils/numberFormatter';

interface VirtualPortfolioExtended extends VirtualPortfolio {
  price?: number;
  customerPrice?: number;
  pricePerTonne?: number;
  title?: string;
  currency?: string;
}

interface State {
  virtualPortfolios: VirtualPortfolioExtended[];
  isFetching: boolean;
}

const initialState: State = {
  virtualPortfolios: [],
  isFetching: false,
};

const slice = createSlice({
  name: 'portfolioSelect',
  initialState,
  reducers: {
    reset: (): State => {
      return { ...initialState };
    },
    setVirtualPortfolios: (state: State, action: PayloadAction<VirtualPortfolioExtended[]>) => {
      state.virtualPortfolios = action.payload;
      return state;
    },
    setWeightForPortfolio: (state: State, action: PayloadAction<{ id: string; tonnes: number }>) => {
      const portfolioToChange = state.virtualPortfolios.find(p => p.id === action.payload.id);
      if (portfolioToChange) {
        portfolioToChange.weight = action.payload.tonnes;
      }
      return state;
    },

    setPriceForPortfolio: (state: State, action: PayloadAction<{ id: string; price: number }>) => {
      const portfolioToChange = state.virtualPortfolios.find(p => p.id === action.payload.id);
      if (portfolioToChange) {
        portfolioToChange.price = action.payload.price;
      }
      return state;
    },
    setCustomerPriceForPortfolio: (state: State, action: PayloadAction<{ id: string; price: number }>) => {
      const portfolioToChange = state.virtualPortfolios.find(p => p.id === action.payload.id);
      if (portfolioToChange) {
        portfolioToChange.customerPrice = action.payload.price;
      }
      return state;
    },
    resetPricesForPortfolios: (state: State) => {
      const mappedPortfolios = state.virtualPortfolios.map(portfolio => ({
        ...portfolio,
        customerPrice: undefined,
        price: undefined,
        weight: 0,
      }));
      state.virtualPortfolios = mappedPortfolios;
      return state;
    },
    resetCustomerPricesForPortfolios: (state: State) => {
      const mappedPortfolios = state.virtualPortfolios.map(portfolio => ({
        ...portfolio,
        customerPrice: undefined,
      }));
      state.virtualPortfolios = mappedPortfolios;
      return state;
    },
    setIsFetching: (state: State, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
      return state;
    },
    setPricePerTonneForPortfolio: (state: State, action: PayloadAction<{ id: string; pricePerTonne: number }>) => {
      const portfolioToChange = state.virtualPortfolios.find(p => p.id === action.payload.id);
      if (portfolioToChange) {
        portfolioToChange.pricePerTonne = action.payload.pricePerTonne;
      }
      return state;
    },
  },
});

const {
  reset,
  setVirtualPortfolios,
  setWeightForPortfolio,
  setPriceForPortfolio,
  setIsFetching,
  setCustomerPriceForPortfolio,
  resetPricesForPortfolios,
  resetCustomerPricesForPortfolios,
  setPricePerTonneForPortfolio,
} = slice.actions;
export {
  reset,
  setVirtualPortfolios,
  setPriceForPortfolio,
  setIsFetching,
  setCustomerPriceForPortfolio,
  resetPricesForPortfolios,
  resetCustomerPricesForPortfolios,
  setWeightForPortfolio,
  setPricePerTonneForPortfolio,
};
export default slice.reducer;

export const resetPortfolioState = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const { allPortfolios } = getState().projects;
  const context = appContextSelector(getState());
  const currentUser = currentUserSelector(getState());
  const currency = (context === 'connect' ? currentUser?.partnership?.currency : currentUser?.customer?.payment?.currency) ?? 'USD';

  dispatch(setIsFetching(true));

  try {
    const shareData =
      allPortfolios?.map(p => {
        return {
          id: p.portfolioData.id ?? '',
          title: p.portfolioData.title ?? '',
          weight: 0,
          price: 0,
          pricePerTonne: 0,
          currency,
        };
      }) ?? [];

    dispatch(setVirtualPortfolios(shareData));
    dispatch(setIsFetching(false));
  } catch (err) {
    dispatch(setIsFetching(false));
  }
};

export const setTonnesForPortfolio = (id: string, specifiedWeight: number, orderTotal: number): AppThunk => (dispatch, getState): void => {
  const { virtualPortfolios } = getState().offsetPortfolioSelect;
  const { allPortfolios } = getState().projects;
  const adjustedSpecifiedWeight = fixFloat(specifiedWeight);
  const adjustedOrderTotal = fixFloat(orderTotal);
  const remainingTotal = fixFloat(virtualPortfolios.filter(p => p.id !== id).reduce((prev, next) => prev + next.weight, 0));
  const targetPortfolio = allPortfolios?.find(p => p.portfolioData.id === id);
  const adjustedTargetPortfolioSupply = targetPortfolio?.portfolioData.totalTonnesCo2Available ?? 0;
  const tonnes = Math.min(adjustedTargetPortfolioSupply, adjustedSpecifiedWeight);
  const amountFromRoundings = adjustedOrderTotal - (tonnes + remainingTotal);
  // if there is enough tonnage in the order, just set it and forget it; it also checks roundings for values displayed in input
  // - rounding is 0.01 and it checks for half of this value
  if (tonnes < adjustedOrderTotal - remainingTotal && Math.abs(amountFromRoundings) >= 0.005) {
    dispatch(setWeightForPortfolio({ id, tonnes }));
    // if the user enters more tonnes than the entire order, then just set this portfolio to max and the rest to 0
  } else if (tonnes > adjustedOrderTotal) {
    for (const p of virtualPortfolios) {
      if (p.id === id) {
        dispatch(setWeightForPortfolio({ id, tonnes: adjustedOrderTotal }));
      } else {
        dispatch(setWeightForPortfolio({ id: p.id, tonnes: 0 }));
      }
    }
    // else - meaning if the user enters more than the remaining tonnage from the order,
    // but it's less than the whole order, then borrow the tonnes from other portfolios
    // until everything is in balance
    // besides it balances roundings
  } else {
    let missingAmount = fixFloat(tonnes - (adjustedOrderTotal - remainingTotal));

    while (missingAmount !== 0) {
      for (const p of virtualPortfolios) {
        if (p.id === id) {
          continue;
        }

        if (p.weight >= Math.abs(missingAmount)) {
          dispatch(setWeightForPortfolio({ id: p.id, tonnes: fixFloat(p.weight - missingAmount) }));
          missingAmount = 0;
        } else {
          missingAmount = fixFloat(missingAmount - p.weight);
          dispatch(setWeightForPortfolio({ id: p.id, tonnes: 0 }));
        }
      }
    }

    for (const vp of virtualPortfolios) {
      const availableSupply = allPortfolios?.find(p => p.portfolioData.id === vp.id)?.portfolioData.totalTonnesCo2Available ?? 0;
      if (vp.weight > availableSupply) {
        dispatch(setWeightForPortfolio({ id: vp.id, tonnes: availableSupply }));
      }
    }

    dispatch(setWeightForPortfolio({ id, tonnes }));
  }
};

export const distributeOrderTonnage = (weight: number): AppThunk => (dispatch, getState): void => {
  const { virtualPortfolios } = getState().offsetPortfolioSelect;
  const { allPortfolios } = getState().projects;
  const firstPortfolio = allPortfolios && allPortfolios?.length ? allPortfolios[0] : undefined;

  const adjustedFirstPortfolioSupply = firstPortfolio?.portfolioData.totalTonnesCo2Available ?? 0;

  if (adjustedFirstPortfolioSupply >= weight) {
    dispatch(setWeightForPortfolio({ id: firstPortfolio?.portfolioData.id ?? '', tonnes: weight }));
    return;
  }

  dispatch(
    setWeightForPortfolio({
      id: firstPortfolio?.portfolioData.id ?? '',
      tonnes: adjustedFirstPortfolioSupply,
    }),
  );

  let missingAmount = weight - adjustedFirstPortfolioSupply;

  while (missingAmount > 0) {
    for (const vp of virtualPortfolios) {
      if (vp.id === firstPortfolio?.portfolioData.id) {
        continue;
      }
      const adjustedAvailableSupply = allPortfolios?.find(p => p.portfolioData.id === vp.id)?.portfolioData.totalTonnesCo2Available ?? 0;

      if (adjustedAvailableSupply >= missingAmount) {
        dispatch(setWeightForPortfolio({ id: vp.id, tonnes: missingAmount }));
        missingAmount = 0;
      } else {
        missingAmount -= adjustedAvailableSupply;
        dispatch(setWeightForPortfolio({ id: vp.id, tonnes: adjustedAvailableSupply }));
      }
    }
  }
};

export const initPortfolioState = (orderTotal: number): AppThunk => (dispatch, getState) => {
  const mainPortfolio = getState().projects.portfolioData;
  const mainPortfolioId = mainPortfolio?.id;
  const supplyForMainPortfolio = mainPortfolio?.totalTonnesCo2Available ?? 0;
  const prevShareData = getState().offsetPortfolioSelect.virtualPortfolios;
  const { allPortfolios } = getState().projects;

  try {
    const isTotalSupplySufficient = (allPortfolios?.reduce((prev, next) => prev + next.portfolioData.totalTonnesCo2Available, 0) ?? 0) >= orderTotal;

    if (mainPortfolioId && supplyForMainPortfolio >= orderTotal) {
      const virtualPortfolios = prevShareData.map(
        (item): VirtualPortfolioExtended => {
          return mainPortfolioId === item.id
            ? {
                ...item,
                weight: orderTotal,
              }
            : { ...item, weight: 0 };
        },
      );

      dispatch(setVirtualPortfolios(virtualPortfolios));
      return;
    }

    const portfolioWithSufficientSupply = allPortfolios?.find(p => p.portfolioData.totalTonnesCo2Available >= orderTotal);

    if (portfolioWithSufficientSupply) {
      const virtualPortfolios = prevShareData.map(
        (item): VirtualPortfolioExtended => {
          return portfolioWithSufficientSupply.portfolioData.id === item.id
            ? {
                ...item,
                weight: orderTotal,
              }
            : { ...item, weight: 0 };
        },
      );
      dispatch(setVirtualPortfolios(virtualPortfolios));
      return;
    } else if (isTotalSupplySufficient) {
      dispatch(distributeOrderTonnage(orderTotal));
      return;
    }
    throw new Error('Insufficient supply');
  } catch (err) {
    dispatch(
      showToast({
        variant: 'error',
        titlei18nKey: 'offset:corporate.portfolios.errorTitle',
        descriptioni18nKey: 'offset:corporate.portfolios.errorMessage',
      }),
    );
  }
};

export const distributeEqually = (portfolioId: string, totalAmount: number, removePortfolio = false): AppThunk => (dispatch, getState) => {
  const { virtualPortfolios } = getState().offsetPortfolioSelect;
  const chosenPortfolioHasWeight = virtualPortfolios.some(({ weight, id }) => weight > 0 && portfolioId === id);
  if (chosenPortfolioHasWeight && !removePortfolio) return;
  const chosenPortfoliosAmount = removePortfolio
    ? virtualPortfolios.filter(({ weight, id }) => weight > 0 && portfolioId !== id).length
    : virtualPortfolios.filter(({ weight, id }) => weight > 0 || (weight === 0 && portfolioId === id)).length;
  const percentage = Number((1 / chosenPortfoliosAmount).toFixed(4));
  const weight = totalAmount * percentage;
  const spareAmount = Number((1 - percentage * chosenPortfoliosAmount).toFixed(4)) * totalAmount;
  const newVirtualPortfolios = virtualPortfolios.map(portfolio =>
    !!portfolio.weight || portfolioId === portfolio.id
      ? removePortfolio && portfolioId === portfolio.id
        ? {
            ...portfolio,
            weight: 0,
          }
        : {
            ...portfolio,
            weight,
          }
      : portfolio,
  );
  if (spareAmount) {
    const index = newVirtualPortfolios.findIndex(({ weight }) => weight > 0);
    const chosenPortfolio = newVirtualPortfolios[index];
    newVirtualPortfolios[index] = {
      ...chosenPortfolio,
      weight: chosenPortfolio.weight + spareAmount,
    };
  }
  dispatch(setVirtualPortfolios(newVirtualPortfolios));
};

export const totalAllocatedSelector = createSelector(
  (state: RootState) => state.offsetPortfolioSelect,
  offsetState => {
    const { virtualPortfolios } = offsetState;

    return fixFloat(virtualPortfolios.reduce((prev, next) => prev + next.weight, 0));
  },
);

export const totalPriceSelector = createSelector(
  (state: RootState) => state.offsetPortfolioSelect,
  offsetState => {
    const { virtualPortfolios } = offsetState;

    return fixFloat(virtualPortfolios.reduce((prev, next) => prev + (next?.price ?? 0), 0));
  },
);

export const totalCustomerPriceSelector = createSelector(
  (state: RootState) => state.offsetPortfolioSelect,
  offsetState => {
    const { virtualPortfolios } = offsetState;

    return roundNumberToTwo(virtualPortfolios.reduce((prev, next) => prev + (next?.customerPrice ?? next?.price ?? 0), 0));
  },
);

export const virtualPortfoliosWithPriceSelector = createSelector(
  (state: RootState) => state.offsetPortfolioSelect,

  offsetState => offsetState.virtualPortfolios ?? [],
);

export const virtualPortfoliosSelector = createSelector(
  (state: RootState) => state.offsetPortfolioSelect,
  offsetState => {
    const { virtualPortfolios } = offsetState;

    return virtualPortfolios
      .filter(p => p.weight > 0)
      .map(({ id: portfolioId, weight }) => ({
        portfolioId,
        weight,
      }));
  },
);

export const singlePorfolioSanityIdSelector = createSelector(
  (state: RootState) => state.offsetPortfolioSelect,
  offsetState => {
    const { virtualPortfolios } = offsetState;

    let mainPortfolioId: string | undefined;

    for (const p of virtualPortfolios) {
      if (p.weight > 0) {
        if (mainPortfolioId) {
          return undefined;
        }

        mainPortfolioId = p.id;
      }
    }

    return mainPortfolioId;
  },
);

export const areVirtualPortfoliosEmptySelector = createSelector(
  (state: RootState) => state.offsetPortfolioSelect,
  offsetState => {
    if (!offsetState.virtualPortfolios.length) {
      return true;
    }

    for (const vp of offsetState.virtualPortfolios) {
      if (!vp.id) {
        return true;
      }
    }

    return false;
  },
);

export const isFetchingPricesSelector = createSelector(
  (state: RootState) => state.offsetPortfolioSelect,

  offsetState => offsetState.isFetching,
);
